import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import { countEventRegistrations } from '@/lib/eventRegistrations';
import {
  checkoutEventRegistration,
  eventPaymentLabel,
  getEventPaymentAvailability,
  type EventPaymentAvailability,
} from '@/lib/eventPayment';
import {
  FEEXPAY_MIN_AMOUNT,
  resolveFeexPayNetworks,
  type FeexPayConfig,
} from '@/lib/feexpay';
import {
  fetchMerchantEvent, fetchMerchantEvents, eventImage, eventDateLabel, eventEndLabel,
  eventTimeLabel, eventTotal, isEventPast, isEventPaid, type PublicEvent,
} from '@/lib/publicEvents';

interface RegisterForm {
  fullname: string;
  email: string;
  phone: string;
  seats: string;
  note: string;
  network: string;
  msisdn: string;
  country: string;
}

const EMPTY_REGISTER: RegisterForm = {
  fullname: '', email: '', phone: '', seats: '1', note: '', network: '', msisdn: '', country: '',
};

function money(value: number, currency: string): string {
  return `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;
}

export default function DetailEventPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant, isTenant, loading: tenantLoading } = useTenant();

  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [others, setOthers] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [registered, setRegistered] = useState(0);

  // Inscription
  const [registerOpen, setRegisterOpen] = useState(false);
  const [form, setForm] = useState<RegisterForm>(EMPTY_REGISTER);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState<{ title: string; message: string; pending: boolean } | null>(null);

  // Paiement en ligne
  const [availability, setAvailability] = useState<EventPaymentAvailability | null>(null);
  const [payChoice, setPayChoice] = useState<'online' | 'offline'>('offline');
  const [payConfig, setPayConfig] = useState<FeexPayConfig | null>(null);

  useThemePageTracking('evenement', tenant?.id ?? null, isTenant && !!tenant && !!event);

  useEffect(() => {
    if (tenantLoading) return;
    if (!id) { setError('Événement introuvable'); setLoading(false); return; }
    if (!isTenant || !tenant) { setError('Événement introuvable'); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const found = await fetchMerchantEvent(tenant.id, Number(id));
        if (cancelled) return;
        if (!found) { setError('Événement introuvable ou non publié'); setLoading(false); return; }
        setEvent(found);
        const all = await fetchMerchantEvents(tenant.id);
        if (!cancelled) setOthers(all.filter((e) => e.id !== found.id).slice(0, 3));
        const seats = await countEventRegistrations(found.id);
        if (!cancelled) setRegistered(seats);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isTenant, tenant, tenantLoading]);

  useEffect(() => {
    if (event?.title) document.title = event.title;
  }, [event]);

  // Disponibilité du paiement en ligne (autorisation SuperAdmin) + opérateurs.
  useEffect(() => {
    if (!event) return;
    let active = true;
    (async () => {
      const result = await getEventPaymentAvailability(event.id_user);
      if (!active) return;
      setAvailability(result);
      if (result.online) {
        setPayChoice('online');
        const { data } = await supabase
          .from('payment_providers')
          .select('config')
          .eq('provider', 'feexpay')
          .maybeSingle();
        const cfg = (data?.config || null) as FeexPayConfig | null;
        if (!active) return;
        setPayConfig(cfg);
        const defaultCountry = String((cfg as Record<string, unknown> | null)?.country || '').toUpperCase();
        setForm((prev) => ({ ...prev, country: defaultCountry }));
      } else {
        setPayChoice('offline');
      }
    })();
    return () => { active = false; };
  }, [event]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: event?.title || 'Événement', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* annulé par l'utilisateur */
    }
  };

  const openRegister = () => {
    setFormError('');
    setSuccess(null);
    setRegisterOpen(true);
  };

  const seatsValue = Math.max(1, Number(form.seats) || 1);
  const currency = event?.currency || 'EUR';
  const total = event ? eventTotal(event, seatsValue) : 0;
  const paid = !!event && isEventPaid(event);

  const operators = useMemo(() => {
    if (!payConfig) return [];
    return resolveFeexPayNetworks(payConfig).filter((o) => o.enabled && o.country === form.country);
  }, [payConfig, form.country]);

  const submitRegistration = async () => {
    if (!event) return;
    if (!form.fullname.trim()) {
      setFormError('Indiquez votre nom complet pour réserver votre place.');
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      setFormError('Un email ou un téléphone est nécessaire pour vous confirmer la place.');
      return;
    }
    const cap = event.capacity != null ? Number(event.capacity) : null;
    if (event.registration_open === false) {
      setFormError('Les inscriptions à cet événement sont fermées.');
      return;
    }
    if (cap != null && registered + seatsValue > cap) {
      const left = Math.max(0, cap - registered);
      setFormError(left > 0 ? `Il ne reste que ${left} place${left > 1 ? 's' : ''} disponible${left > 1 ? 's' : ''}.` : 'Cet événement est complet.');
      return;
    }

    const useOnline = paid && payChoice === 'online' && availability?.online === true;
    if (useOnline) {
      if (!form.network) { setFormError('Choisissez votre opérateur Mobile Money.'); return; }
      if ((form.msisdn || form.phone).replace(/\D/g, '').length < 8) {
        setFormError('Indiquez le numéro Mobile Money qui sera débité.');
        return;
      }
    }

    setSaving(true);
    setFormError('');
    try {
      const result = await checkoutEventRegistration({
        eventId: event.id,
        fullname: form.fullname,
        email: form.email,
        phone: form.phone,
        seats: seatsValue,
        note: form.note,
        payOnline: useOnline,
        network: useOnline ? form.network : undefined,
        country: useOnline ? form.country : undefined,
        phoneNumber: useOnline ? (form.msisdn || form.phone) : undefined,
      });

      if (!result.ok) {
        setFormError(result.error || "L'inscription n'a pas pu aboutir.");
        return;
      }

      setRegistered((prev) => prev + seatsValue);
      if (result.pending) {
        setSuccess({
          title: 'Demande de paiement envoyée',
          message: result.instructions || 'Validez le paiement sur votre téléphone pour confirmer votre place.',
          pending: true,
        });
      } else if (result.offline) {
        setSuccess({
          title: 'Inscription confirmée',
          message: `Votre place est réservée. Le règlement de ${money(result.amount || total, result.currency || currency)} se fait sur place.`,
          pending: false,
        });
      } else {
        setSuccess({
          title: 'Inscription confirmée',
          message: "Votre place est bien notée. L'organisateur vous recontactera pour les détails.",
          pending: false,
        });
      }
      setForm(EMPTY_REGISTER);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "L'inscription n'a pas pu aboutir.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-calendar-event-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Événement introuvable</h1>
        <p className="text-sm text-foreground-500 mb-6">Cet événement n&apos;existe pas ou n&apos;est plus publié.</p>
        <Link
          to="/events"
          className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          Voir tous les événements
        </Link>
      </div>
    );
  }

  const cover = eventImage(event);
  const startLabel = eventDateLabel(event.start_datetime);
  const startTime = eventTimeLabel(event.start_datetime);
  const endTime = eventTimeLabel(event.end_datetime);
  const endDate = eventEndLabel(event.end_datetime);
  const past = isEventPast(event);
  const capacity = event.capacity != null ? Number(event.capacity) : null;
  const remaining = capacity != null ? Math.max(0, capacity - registered) : null;
  const full = capacity != null && remaining !== null && remaining <= 0;
  const registrationsOpen = event.registration_open !== false;
  const closed = past || !registrationsOpen || full;

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-10">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/events" className="hover:text-foreground-700 no-underline whitespace-nowrap">Événements</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600 truncate max-w-[220px]">{event.title || 'Événement'}</span>
        </nav>

        {/* Bandeau */}
        <div className="relative rounded-xl overflow-hidden border border-background-200/70 bg-background-100">
          <div className="relative h-[240px] md:h-[340px] flex items-center justify-center">
            {cover ? (
              <img
                src={cover}
                alt={event.title || 'Événement'}
                title={`${event.title || 'Événement'} — agenda`}
                className="w-full h-full object-cover object-top"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 via-background-100 to-accent-50">
                <i className="ri-calendar-event-line text-6xl text-secondary-300"></i>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold">
                  {past ? 'Événement terminé' : full ? 'Complet' : 'À venir'}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-800 text-[11px] font-semibold">
                  <i className="ri-calendar-line mr-1"></i>{startLabel}
                </span>
                {paid ? (
                  <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-[11px] font-bold">
                    <i className="ri-price-tag-3-line mr-1"></i>{money(Number(event.price), currency)} / place
                  </span>
                ) : (
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold">
                    <i className="ri-gift-line mr-1"></i>Entrée gratuite
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 leading-tight">
                {event.title || 'Événement'}
              </h1>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Contenu */}
          <div className="lg:col-span-2">
            <h2 className="text-sm font-bold text-foreground-950 mb-3">À propos de cet événement</h2>
            {event.description ? (
              <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{event.description}</p>
            ) : (
              <p className="text-sm text-foreground-500">Le marchand n&apos;a pas encore ajouté de description pour cet événement.</p>
            )}

            {paid && (
              <div className="mt-5 rounded-lg border border-primary-200/70 bg-primary-50/60 p-4">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-secure-payment-line"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950">
                      Tarif : {money(Number(event.price), currency)} par place
                    </p>
                    <p className="text-xs text-foreground-600 mt-1">
                      {availability?.online
                        ? `Paiement en ligne disponible (${availability.methods.map(eventPaymentLabel).join(', ')}) ou règlement sur place.`
                        : availability?.reason || 'Règlement sur place auprès de l’organisateur.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {!closed ? (
                <button
                  type="button"
                  onClick={openRegister}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-ticket-2-line"></i>
                  {paid ? 'Réserver ma place' : "S'inscrire à l'événement"}
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-foreground-500 text-xs font-semibold whitespace-nowrap">
                  <i className="ri-calendar-check-line"></i>
                  {full ? 'Complet — inscriptions fermées' : 'Inscriptions closes'}
                </span>
              )}
              <button
                type="button"
                onClick={share}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-xs font-semibold whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <i className={copied ? 'ri-check-line text-accent-600' : 'ri-share-forward-line'}></i>
                {copied ? 'Lien copié' : 'Partager'}
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors no-underline"
              >
                <i className="ri-mail-line"></i>
                Contacter l&apos;organisateur
              </Link>
            </div>
          </div>

          {/* Infos pratiques */}
          <div>
            <div className="lg:sticky lg:top-6 bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h2 className="text-sm font-bold text-foreground-950 mb-4">Informations pratiques</h2>
              <ul className="space-y-3.5">
                <li className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-sm"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Date</span>
                    <span className="block text-sm text-foreground-800">{startLabel}</span>
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-sm"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Horaire</span>
                    <span className="block text-sm text-foreground-800">
                      {startTime || 'Horaire à confirmer'}
                      {endTime ? ` — ${endTime}` : ''}
                    </span>
                  </span>
                </li>
                {endDate && (
                  <li className="flex items-start gap-2.5">
                    <span className="w-8 h-8 rounded-md bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-check-line text-sm"></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Fin</span>
                      <span className="block text-sm text-foreground-800">{endDate}</span>
                    </span>
                  </li>
                )}
                {event.location && (
                  <li className="flex items-start gap-2.5">
                    <span className="w-8 h-8 rounded-md bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-sm"></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Lieu</span>
                      <span className="block text-sm text-foreground-800">{event.location}</span>
                    </span>
                  </li>
                )}
                <li className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-price-tag-3-line text-sm"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Tarif</span>
                    <span className="block text-sm font-semibold text-foreground-800">
                      {paid ? `${money(Number(event.price), currency)} / place` : 'Gratuit'}
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-md bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                    <i className="ri-store-2-line text-sm"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Organisé par</span>
                    <span className="block text-sm text-foreground-800">{tenant?.nomcommerce || tenant?.name || 'Le commerce'}</span>
                    {tenant?.Ville && <span className="block text-xs text-foreground-500">{tenant.Ville}</span>}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-group-line text-sm"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide font-semibold text-foreground-400">Inscrits</span>
                    <span className="block text-sm text-foreground-800">
                      {registered > 0 ? `${registered} place${registered > 1 ? 's' : ''} réservée${registered > 1 ? 's' : ''}` : 'Soyez le premier à vous inscrire'}
                      {capacity != null ? ` sur ${capacity}` : ''}
                    </span>
                    {remaining !== null && (
                      <span className={`block text-xs mt-0.5 font-medium ${full ? 'text-red-500' : 'text-accent-700'}`}>
                        {full ? 'Aucune place restante' : `Il reste ${remaining} place${remaining > 1 ? 's' : ''}`}
                      </span>
                    )}
                  </span>
                </li>
              </ul>

              {!closed && (
                <button
                  type="button"
                  onClick={openRegister}
                  className="mt-4 w-full h-11 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-ticket-2-line"></i>
                  {paid ? 'Réserver et payer' : 'Je m’inscris'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Autres événements */}
        {others.length > 0 && (
          <section className="mt-12">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-base font-bold font-heading text-foreground-950">Autres événements</h2>
              <Link to="/events" className="text-xs font-semibold text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap inline-flex items-center gap-1">
                Tout voir <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((ev) => {
                const img = eventImage(ev);
                return (
                  <Link
                    key={ev.id}
                    to={`/events/${ev.id}`}
                    className="group flex items-center gap-3 p-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-background-300/60 transition-colors no-underline"
                  >
                    <div className="w-16 h-16 rounded-md overflow-hidden bg-background-100 flex-shrink-0 flex items-center justify-center">
                      {img ? (
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <i className="ri-calendar-event-line text-xl text-foreground-300"></i>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                        {ev.title || 'Événement'}
                      </p>
                      <p className="text-[11px] text-foreground-500 truncate mt-0.5">
                        <i className="ri-calendar-line mr-1"></i>{eventDateLabel(ev.start_datetime)}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Modal d'inscription */}
      {registerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/45" onClick={() => (saving ? undefined : setRegisterOpen(false))}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 sticky top-0 bg-background-50 z-10">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-ticket-2-line text-primary-500"></i>
                {paid ? 'Réserver ma place' : "Inscription à l'événement"}
              </h3>
              <button
                type="button"
                onClick={() => (saving ? undefined : setRegisterOpen(false))}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {success ? (
              <div className="p-6 text-center">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${success.pending ? 'bg-secondary-100' : 'bg-accent-100'}`}>
                  <i className={`text-2xl ${success.pending ? 'ri-smartphone-line text-secondary-700' : 'ri-checkbox-circle-line text-accent-700'}`}></i>
                </div>
                <h4 className="text-base font-bold font-heading text-foreground-950 mb-1">{success.title}</h4>
                <p className="text-sm text-foreground-600 mb-5">{success.message}</p>
                <button
                  type="button"
                  onClick={() => setRegisterOpen(false)}
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                      Nom complet <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.fullname}
                      onChange={(e) => setForm({ ...form, fullname: e.target.value })}
                      placeholder="Votre nom"
                      className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Email</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="vous@exemple.com"
                        className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Téléphone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+212 6 00 00 00 00"
                        className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nombre de places</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, seats: String(Math.max(1, seatsValue - 1)) })}
                        className="w-10 h-11 rounded-lg border border-background-200/70 bg-background-50 text-foreground-700 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={form.seats}
                        onChange={(e) => setForm({ ...form, seats: e.target.value })}
                        className="w-16 h-11 text-center rounded-lg border border-background-200/70 bg-background-50 text-sm font-semibold text-foreground-950 focus:outline-none focus:border-primary-300"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, seats: String(seatsValue + 1) })}
                        className="w-10 h-11 rounded-lg border border-background-200/70 bg-background-50 text-foreground-700 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                      {paid && (
                        <span className="ml-auto text-sm font-bold text-primary-700 whitespace-nowrap">
                          {money(total, currency)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Choix du paiement */}
                  {paid && (
                    <div className="rounded-lg border border-background-200/70 p-3 space-y-2.5">
                      <p className="text-xs font-semibold text-foreground-700">Paiement</p>
                      {availability?.online && (
                        <button
                          type="button"
                          onClick={() => setPayChoice('online')}
                          className={`w-full flex items-start gap-2.5 p-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                            payChoice === 'online' ? 'border-primary-300 bg-primary-50/60' : 'border-background-200/70 bg-background-50 hover:bg-background-100/60'
                          }`}
                        >
                          <i className={`${payChoice === 'online' ? 'ri-radio-button-line text-primary-600' : 'ri-checkbox-blank-circle-line text-foreground-400'} mt-0.5`}></i>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-foreground-900">Payer maintenant</span>
                            <span className="block text-[11px] text-foreground-500">
                              {availability.methods.map((m) => `${eventPaymentLabel(m)}`).join(' · ')} — place confirmée immédiatement
                            </span>
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setPayChoice('offline')}
                        className={`w-full flex items-start gap-2.5 p-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                          payChoice === 'offline' ? 'border-primary-300 bg-primary-50/60' : 'border-background-200/70 bg-background-50 hover:bg-background-100/60'
                        }`}
                      >
                        <i className={`${payChoice === 'offline' ? 'ri-radio-button-line text-primary-600' : 'ri-checkbox-blank-circle-line text-foreground-400'} mt-0.5`}></i>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground-900">Réserver sans payer</span>
                          <span className="block text-[11px] text-foreground-500">Vous réglez sur place auprès de l&apos;organisateur</span>
                        </span>
                      </button>

                      {payChoice === 'online' && availability?.online && (
                        <div className="pt-1 space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Pays</label>
                              <select
                                value={form.country}
                                onChange={(e) => setForm({ ...form, country: e.target.value, network: '' })}
                                className="w-full h-10 px-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer"
                              >
                                <option value="">— Choisir —</option>
                                {Array.from(new Set(resolveFeexPayNetworks(payConfig).filter((o) => o.enabled).map((o) => o.country))).map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Opérateur</label>
                              <select
                                value={form.network}
                                onChange={(e) => setForm({ ...form, network: e.target.value })}
                                className="w-full h-10 px-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer"
                              >
                                <option value="">— Choisir —</option>
                                {operators.map((o) => (
                                  <option key={`${o.country}-${o.slug}`} value={o.slug}>{o.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Numéro Mobile Money</label>
                            <input
                              type="tel"
                              value={form.msisdn}
                              onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
                              placeholder="Numéro qui sera débité"
                              className="w-full h-10 px-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                            />
                            <p className="text-[10px] text-foreground-400 mt-1 flex items-center gap-1">
                              <i className="ri-information-line"></i>
                              Le montant est converti en franc CFA si nécessaire (minimum {FEEXPAY_MIN_AMOUNT} XOF).
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Message (optionnel)</label>
                    <textarea
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      rows={3}
                      maxLength={500}
                      placeholder="Une question, une contrainte alimentaire…"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 resize-none"
                    />
                  </div>

                  {formError && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
                  )}
                </div>

                <div className="px-5 pb-5">
                  <button
                    type="button"
                    onClick={() => void submitRegistration()}
                    disabled={saving}
                    className="w-full h-12 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                    {paid && payChoice === 'online' && availability?.online
                      ? `Payer ${money(total, currency)}`
                      : paid
                        ? 'Réserver ma place'
                        : 'Confirmer mon inscription'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
